import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Share2, RotateCcw } from 'lucide-react';
import { quizQuestions, quizResults } from '../data/universeData';

const CrewQuiz = () => {
  const [isStarted, setIsStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const calculateResult = (finalAnswers) => {
    const scores = { CMD: 0, CONSIGLIERE: 0, BURNER: 0, EVOLUTION: 0 };

    // Q1: Block goes quiet
    if (finalAnswers[1] === 'A') scores.CMD += 2;
    if (finalAnswers[1] === 'B') scores.CONSIGLIERE += 2;
    if (finalAnswers[1] === 'C') scores.CONSIGLIERE += 1; scores.EVOLUTION += 1;
    if (finalAnswers[1] === 'D') scores.BURNER += 2;

    // Q2: Loyalty means
    if (finalAnswers[2] === 'A') scores.CMD += 2;
    if (finalAnswers[2] === 'B') scores.CONSIGLIERE += 2;
    if (finalAnswers[2] === 'C') scores.CMD += 1; scores.CONSIGLIERE += 1;
    if (finalAnswers[2] === 'D') scores.EVOLUTION += 2;

    // Q3: Dirty money
    if (finalAnswers[3] === 'A') scores.CONSIGLIERE += 2;
    if (finalAnswers[3] === 'B') scores.CMD += 2;
    if (finalAnswers[3] === 'C') scores.BURNER += 2;
    if (finalAnswers[3] === 'D') scores.EVOLUTION += 2;

    // Q4: Feds watching
    if (finalAnswers[4] === 'A') scores.EVOLUTION += 2;
    if (finalAnswers[4] === 'B') scores.CMD += 2;
    if (finalAnswers[4] === 'C') scores.CONSIGLIERE += 2;
    if (finalAnswers[4] === 'D') scores.BURNER += 2;

    // Q5: Water remembers
    if (finalAnswers[5] === 'A') scores.CONSIGLIERE += 2;
    if (finalAnswers[5] === 'B') scores.EVOLUTION += 2;
    if (finalAnswers[5] === 'C') scores.BURNER += 2;
    if (finalAnswers[5] === 'D') scores.CMD += 2;

    const maxScore = Math.max(...Object.values(scores));
    const winner = Object.keys(scores).find(key => scores[key] === maxScore);
    return quizResults[winner];
  };

  const handleAnswer = (optionId) => {
    const newAnswers = { ...answers, [currentQuestion + 1]: optionId };
    setAnswers(newAnswers);

    if (currentQuestion < quizQuestions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      setTimeout(() => setResult(calculateResult(newAnswers)), 300);
    }
  };

  const handleShare = () => {
    const text = `I run with ${result.name} — find your crew. #KillingOverWater`;
    if (navigator.share) {
      navigator.share({ text });
    } else {
      navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    }
  };

  const resetQuiz = () => {
    setIsStarted(false);
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
  };

  return (
    <section 
      id="quiz" 
      data-testid="quiz-section"
      className="py-24 px-6 bg-[#080C10]"
    >
      <div className="max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          {!isStarted ? (
            // Start Screen
            <motion.div
              key="start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
              data-testid="quiz-start"
            >
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#C9A84C] mb-4">
                WHICH SIDE ARE YOU ON?
              </h2>
              <p className="font-['Outfit'] text-[#E8E0D0]/60 text-base mb-8 max-w-md mx-auto">
                Answer five questions. Find your faction. The water already knows — but do you?
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsStarted(true)}
                className="bg-[#C9A84C] text-[#080C10] py-3 px-8 rounded font-['Outfit'] font-semibold tracking-wide hover:bg-[#d4b55d] transition-colors"
                data-testid="quiz-start-btn"
              >
                ENTER THE QUIZ
              </motion.button>
            </motion.div>
          ) : result ? (
            // Result Screen
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center"
              data-testid="quiz-result"
            >
              <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-widest uppercase">
                YOUR FACTION
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#C9A84C] mt-2 mb-6">
                {result.name}
              </h2>
              <div className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg p-6 mb-8">
                <p className="font-['Outfit'] text-[#E8E0D0]/80 leading-relaxed">
                  {result.description}
                </p>
              </div>
              <div className="flex gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShare}
                  className="btn-gold py-3 px-6 rounded font-['Outfit'] tracking-wide flex items-center gap-2"
                  data-testid="quiz-share-btn"
                >
                  <Share2 size={18} />
                  SHARE
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetQuiz}
                  className="bg-[#1A2535] text-[#E8E0D0] py-3 px-6 rounded font-['Outfit'] tracking-wide flex items-center gap-2 hover:bg-[#243044] transition-colors"
                  data-testid="quiz-retry-btn"
                >
                  <RotateCcw size={18} />
                  RETRY
                </motion.button>
              </div>
            </motion.div>
          ) : (
            // Question Screen
            <motion.div
              key={`question-${currentQuestion}`}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              data-testid={`quiz-question-${currentQuestion + 1}`}
            >
              {/* Progress */}
              <div className="flex items-center justify-between mb-8">
                <span className="font-['Outfit'] text-[#C9A84C]/60 text-sm tracking-wider">
                  QUESTION {currentQuestion + 1} OF {quizQuestions.length}
                </span>
                <div className="flex gap-1">
                  {quizQuestions.map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full ${
                        i <= currentQuestion ? 'bg-[#C9A84C]' : 'bg-[#333]'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Question */}
              <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[#E8E0D0] mb-8">
                {quizQuestions[currentQuestion].question}
              </h3>

              {/* Options */}
              <div className="space-y-3">
                {quizQuestions[currentQuestion].options.map((option) => (
                  <motion.button
                    key={option.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => handleAnswer(option.id)}
                    className={`w-full text-left quiz-option p-4 rounded-lg ${
                      answers[currentQuestion + 1] === option.id ? 'selected' : ''
                    }`}
                    data-testid={`quiz-option-${option.id}`}
                  >
                    <span className="font-['Outfit'] text-[#C9A84C] mr-3">{option.id})</span>
                    <span className="font-['Outfit'] text-[#E8E0D0]/80">{option.text}</span>
                  </motion.button>
                ))}
              </div>

              {/* Navigation */}
              {currentQuestion > 0 && (
                <button
                  onClick={() => setCurrentQuestion(currentQuestion - 1)}
                  className="mt-6 font-['Outfit'] text-[#E8E0D0]/50 text-sm flex items-center gap-1 hover:text-[#C9A84C] transition-colors"
                  data-testid="quiz-back-btn"
                >
                  <ChevronLeft size={16} />
                  Previous question
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default CrewQuiz;
